/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import FlaggedCommentView from './FlaggedComment.view';
import FormHeaderView from '../../../shared/views/PersonDetail/FormHeader.view';
import { connect } from 'react-redux';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../store/store';
import { FlagSummary } from './shared/FlaggedContentDetailsCommons';
import {
	ClientUserGeneratedContentFlagComment,
	ClientUserGeneratedContentFlagStatusRecord
} from '@DigitaleDoerfer/digitale-doerfer-api';
import FlaggedDetailsActionsMenuContainer from './shared/FlaggedDetailsActionsMenu.container';
import { flaggedContentService } from '../../../ServiceFactory';
import { FlaggedContentActionTypes } from '../store/FlaggedContentActionTypes';
import { RouteComponentProps } from 'react-router-dom';
import { getCommentFlag } from '../store/FlaggedContent.actions';
import MainLoadingIndicator from '../../../shared/views/LoadingIndicators/MainLoadingIndicator.view';
import { Container, Paper } from '@mui/material';
import FlaggedContentDetailsFooterView from './shared/FlaggedContentDetailsFooter.view';

interface Identifiable {
	id: string;
}

interface Props extends RouteComponentProps<Identifiable> {
	loading: boolean;
	commentFlag?: ClientUserGeneratedContentFlagComment;
	statusRecords: ClientUserGeneratedContentFlagStatusRecord[];
	flagSummary: FlagSummary;
	getCommentFlag: (flagId: string) => void;
}

interface State {
	contentRefreshed: boolean;
}

class FlaggedCommentContainer extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { contentRefreshed: false };
	}

	componentDidMount(): void {
		const { getCommentFlag, match } = this.props;
		const { id } = match.params;
		getCommentFlag(id);
		this.setState({ contentRefreshed: true });
	}

	render(): JSX.Element | null {
		const { loading, commentFlag, statusRecords, flagSummary } = this.props;
		const { contentRefreshed } = this.state;

		return (
			<Container fixed maxWidth="md">
				<MainLoadingIndicator loading={loading || !contentRefreshed} />
				{!loading && contentRefreshed && commentFlag && (
					<Paper>
						<FormHeaderView
							title={'Gemeldeter Kommentar'}
							actionMenu={<FlaggedDetailsActionsMenuContainer flagStatus={commentFlag.status} />}
						/>
						<FlaggedCommentView
							commentFlag={commentFlag}
							flagPost={commentFlag.postOfComment}
							flagPostDeleted={commentFlag.postOfCommentDeleted}
							flagSummary={flagSummary}
							statusRecords={statusRecords}
							commentsOfPost={flaggedContentService().getCommentsFromFlaggedPostOrComment(commentFlag)}
						/>
						<FlaggedContentDetailsFooterView />
					</Paper>
				)}
			</Container>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		loading: state.flaggedContent.detail.loading,
		commentFlag: state.flaggedContent.detail.comment,
		statusRecords: state.flaggedContent.detail.statusRecords,
		flagSummary: state.flaggedContent.detail.flagSummary
	};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: PlatformAdminUIThunkType<FlaggedContentActionTypes>) => {
	return {
		getCommentFlag: (flagId: string): Promise<void> => dispatch(getCommentFlag(flagId))
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(FlaggedCommentContainer);
